/**
 * AuditFormDialog — Create/edit dialog for LPA audits.
 *
 * Features:
 * - Dual-mode: create (no audit prop) or edit (audit prop provided)
 * - Owner picker with searchable dropdown (factory users)
 * - Cascading location selector via HierarchySelector
 * - Description textarea
 * - Form validation (name + owner required)
 * - Info tooltips explaining key concepts
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/core/LanguageContext';
import { useFactory } from '@/core/hooks/useFactory';
import { queryKeys } from '@/core/lib/queryKeys';
import { fetchFactoryUsers } from '@/core/api/user-roles';
import { HierarchySelector } from '@/shared/components/selectors/HierarchySelector';
import type { LpaAuditWithDetails } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  Loader2,
  ChevronsUpDown,
  Check,
  Info,
  User,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditFormData {
  name: string;
  description: string;
  owner_id: string;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
}

interface AuditFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, dialog is in "edit" mode. */
  audit?: LpaAuditWithDetails | null;
  /** Called with form data when the user submits. Parent controls the mutation. */
  onSubmit: (data: AuditFormData) => void;
  isSubmitting?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyForm(): AuditFormData {
  return {
    name: '',
    description: '',
    owner_id: '',
    area_id: null,
    line_id: null,
    station_id: null,
  };
}

function formFromAudit(audit: LpaAuditWithDetails): AuditFormData {
  return {
    name: audit.name,
    description: audit.description ?? '',
    owner_id: audit.owner_id,
    area_id: audit.area_id,
    line_id: audit.line_id,
    station_id: audit.station_id,
  };
}

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

// ---------------------------------------------------------------------------
// Owner picker sub-component
// ---------------------------------------------------------------------------

interface OwnerPickerProps {
  value: string;
  onChange: (userId: string) => void;
  disabled?: boolean;
  t: (key: string, fallback: string) => string;
}

function OwnerPicker({ value, onChange, disabled, t }: OwnerPickerProps) {
  const { selectedFactoryId } = useFactory();
  const [open, setOpen] = useState(false);

  const { data: users = [], isLoading } = useQuery({
    queryKey: queryKeys.factoryUsers(selectedFactoryId),
    queryFn: () => fetchFactoryUsers(selectedFactoryId!),
    enabled: !!selectedFactoryId,
  });

  // Approved users only, deduplicated by user_id
  const approvedUsers = useMemo(() => {
    const seen = new Set<string>();
    return users
      .filter((u) => {
        if (u.status !== 'approved') return false;
        if (seen.has(u.user_id)) return false;
        seen.add(u.user_id);
        return true;
      })
      .map((u) => ({
        id: u.user_id,
        full_name: (u.profile as { full_name?: string | null })?.full_name ?? null,
        email: (u.profile as { email?: string })?.email ?? '',
        role: u.role,
      }));
  }, [users]);

  const selectedUser = approvedUsers.find((u) => u.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('mod_lpa.audits.form.loadingUsers', 'Loading users...')}
            </span>
          ) : selectedUser ? (
            <span className="flex items-center gap-2">
              <Avatar className="h-5 w-5">
                <AvatarFallback className="text-[10px]">
                  {getInitials(selectedUser.full_name, selectedUser.email)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">
                {selectedUser.full_name ?? selectedUser.email}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              {t('mod_lpa.audits.form.selectOwner', 'Select owner...')}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t('mod_lpa.audits.form.searchUsers', 'Search by name or email...')}
          />
          <CommandList>
            <CommandEmpty>
              {t('mod_lpa.audits.form.noUsersFound', 'No users found.')}
            </CommandEmpty>
            <CommandGroup>
              {approvedUsers.map((user) => (
                <CommandItem
                  key={user.id}
                  value={`${user.full_name ?? ''} ${user.email}`}
                  onSelect={() => {
                    onChange(user.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === user.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <Avatar className="h-6 w-6 mr-2">
                    <AvatarFallback className="text-[10px]">
                      {getInitials(user.full_name, user.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm truncate">
                      {user.full_name ?? user.email}
                    </span>
                    {user.full_name && (
                      <span className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Info tooltip helper
// ---------------------------------------------------------------------------

function FieldInfo({ children }: { children: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/60 cursor-help" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px] text-xs">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AuditFormDialog({
  open,
  onOpenChange,
  audit,
  onSubmit,
  isSubmitting = false,
}: AuditFormDialogProps) {
  const { t } = useLanguage();
  const isEditing = !!audit;

  const [form, setForm] = useState<AuditFormData>(emptyForm);

  // Reset form when dialog opens or audit changes
  useEffect(() => {
    if (open) {
      setForm(audit ? formFromAudit(audit) : emptyForm());
    }
  }, [open, audit]);

  const updateField = <K extends keyof AuditFormData>(
    field: K,
    value: AuditFormData[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isValid = form.name.trim().length > 0 && form.owner_id.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;
    onSubmit({
      ...form,
      name: form.name.trim(),
      description: form.description.trim() || '',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('mod_lpa.audits.form.editTitle', 'Edit Audit')
              : t('mod_lpa.audits.form.createTitle', 'New Audit')}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t(
                  'mod_lpa.audits.form.editDescription',
                  'Update the audit definition. Changes to location scope only affect future schedule instances.',
                )
              : t(
                  'mod_lpa.audits.form.createDescription',
                  'Define a new audit for this factory. Start as a draft and add schedules before activating.',
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="audit-name">
                {t('mod_lpa.audits.form.nameLabel', 'Audit Name')} *
              </Label>
              <FieldInfo>
                {t(
                  'mod_lpa.audits.form.nameHint',
                  'A descriptive name for this audit, e.g., "Assembly Line A - Daily Check" or "Paint Shop Weekly Audit".',
                )}
              </FieldInfo>
            </div>
            <Input
              id="audit-name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder={t('mod_lpa.audits.form.namePlaceholder', 'e.g., Assembly Line A - Daily Check')}
              autoFocus
              disabled={isSubmitting}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="audit-description">
                {t('mod_lpa.audits.form.descriptionLabel', 'Description')}
              </Label>
              <FieldInfo>
                {t(
                  'mod_lpa.audits.form.descriptionHint',
                  'Optional notes about the purpose or scope of this audit. Visible to supervisors planning schedules.',
                )}
              </FieldInfo>
            </div>
            <Textarea
              id="audit-description"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder={t(
                'mod_lpa.audits.form.descriptionPlaceholder',
                'What this audit covers and why...',
              )}
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          {/* Owner */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label>
                <User className="h-3.5 w-3.5 inline mr-1" />
                {t('mod_lpa.audits.form.ownerLabel', 'Owner')} *
              </Label>
              <FieldInfo>
                {t(
                  'mod_lpa.audits.form.ownerHint',
                  'The person responsible for this audit definition. They manage schedules and receive escalation notifications.',
                )}
              </FieldInfo>
            </div>
            <OwnerPicker
              value={form.owner_id}
              onChange={(id) => updateField('owner_id', id)}
              disabled={isSubmitting}
              t={t}
            />
          </div>

          {/* Location scope */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label>
                {t('mod_lpa.audits.form.locationLabel', 'Location Scope')}
              </Label>
              <FieldInfo>
                {t(
                  'mod_lpa.audits.form.locationHint',
                  'Narrow the audit to a specific area, line, or station. Leave empty to audit the entire factory. Schedules under this audit inherit the scope.',
                )}
              </FieldInfo>
            </div>
            <HierarchySelector
              areaId={form.area_id}
              lineId={form.line_id}
              stationId={form.station_id}
              onAreaChange={(id) => updateField('area_id', id)}
              onLineChange={(id) => updateField('line_id', id)}
              onStationChange={(id) => updateField('station_id', id)}
              includeStation
              allowClear
              disabled={isSubmitting}
              showLabels={false}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('mod_lpa.audits.form.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {isEditing
                ? t('mod_lpa.audits.form.saveChanges', 'Save Changes')
                : t('mod_lpa.audits.form.create', 'Create Audit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
